import React, { useCallback, useEffect, useMemo } from 'react';
import { RouteComponentProps } from 'react-router';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { ClientDatasetsRiskTolerance } from './ClientDatasetRiskTolerance';
import { getSelectedDatasetId, ClientRiskToleranceView, setClientRiskToleranceView, fetchAllRiskToleranceDatasetsStarted, fetchRiskToleranceDatasetDefinitionsStarted, getAllRiskToleranceDatasets, getRiskToleranceDatasetDefinitions, resetRiskToleranceDatasets, getIsFetchingRiskToleranceDatasets } from './store';
import { fetchAllDropdownListsStarted } from '../dropdown-lists/store';
import { fetchAllDocumentNamesStarted } from '../documents/store';
import { DatasetsList } from '../shared/DatasetsList';

export interface ClientRiskToleranceRouteParams {
    datasetId: string | undefined;
}

export const ClientRiskTolerance: React.FC<RouteComponentProps<ClientRiskToleranceRouteParams>> = (props) => {
    const dispatch = useAppDispatch();

    useFetchStarted([
        fetchAllRiskToleranceDatasetsStarted(),
        fetchAllDropdownListsStarted(),
        fetchAllDocumentNamesStarted()
    ]);
    const datasetId = useMemo(() => props.match.params.datasetId ? parseInt(props.match.params.datasetId) : undefined, [props]);
    const parentDatasetId = useAppSelector(getSelectedDatasetId);
    const clientDatasets = useAppSelector(getAllRiskToleranceDatasets);
    const datasetDefinitions = useAppSelector(getRiskToleranceDatasetDefinitions);
    const isFetchingDatasets = useAppSelector(getIsFetchingRiskToleranceDatasets);

    const parentDefinition = useMemo(() => datasetDefinitions.map(definition => definition).find(({ datasetId }) => datasetId === parentDatasetId) || null, [datasetDefinitions, parentDatasetId]);

    const datasetIdInParamsExists = useMemo(() => (datasetId && clientDatasets.map(({ datasetId }) => datasetId).includes(datasetId)) || false, [clientDatasets, datasetId]);

    const openRiskTolerance = useCallback((datasetId: number) => {
        dispatch(fetchRiskToleranceDatasetDefinitionsStarted(datasetId));
        dispatch(setClientRiskToleranceView(ClientRiskToleranceView.CONFIGURE, datasetId));
    }, [dispatch]);

    useEffect(() => {
        if (!parentDefinition && datasetIdInParamsExists) {
            dispatch(fetchRiskToleranceDatasetDefinitionsStarted(datasetId!));
            dispatch(setClientRiskToleranceView(ClientRiskToleranceView.CONFIGURE, datasetId!, false));
        }
    }, [dispatch, datasetIdInParamsExists, parentDefinition, datasetId]);

    const pageContent = useMemo(() => datasetIdInParamsExists ? <ClientDatasetsRiskTolerance datasetId={datasetId!} /> : <DatasetsList datasets={clientDatasets} header='Risk Tolerance' isLoading={isFetchingDatasets} openRiskTolerance={openRiskTolerance} />, [datasetId, datasetIdInParamsExists, clientDatasets, isFetchingDatasets, openRiskTolerance]);

    useEffect(() => () => { dispatch(resetRiskToleranceDatasets()); }, [dispatch]);

    return (
        <div style={{ height: '100%', width: '100%' }}>{pageContent}</div>
    );
};

